/**
 * Form translation contract, browser side (#2109 R2, lane L0 — plan
 * `docs/superpowers/plans/2026-09-23-multilingual-r2-forms.md`, brief B9).
 *
 * The mirror of `backend/src/lib/formI18n.js`: the same entry shape, the same
 * slot keys, the same staleness rule. Both test files pin one sha256 vector
 * (`test/fixtures/formI18n.ts` ↔ `backend/tests/fixtures/form-i18n.js`), so
 * the same English gives the same hash in both stacks.
 *
 * Where the entries sit is documented on the types in `lib/formDefinitions.ts`
 * (`FieldI18nBlock`, `SectionI18nBlock`, `ScreenI18nBlock`, `CopyI18n`).
 *
 * ## Two hash functions, on purpose
 *
 * - `sourceHash` is async Web Crypto (`crypto.subtle.digest`), for callers
 *   that can await: the builder, scripts, anything outside render.
 * - `sourceHashSync` is a small pure sha256. `localizeVersion` runs inside a
 *   render (`useMemo` in `useFormDefinition`), and a render cannot await, so
 *   every function in this file that compares an entry to live English uses
 *   the sync variant. The tests prove the two agree with each other and with
 *   the backend's `node:crypto` on the shared vector.
 */
import type {
  CopyI18n,
  FormDefinitionVersion,
  FormLanguage,
  I18nEntry,
  I18nReviewState,
  TranslationLanguage,
} from './formDefinitions';

/** The five UI catalog codes (R2-B6). Never `fil`: it has no UI catalog. */
export const UI_FORM_LANGUAGES: readonly FormLanguage[] = ['en', 'es', 'pt', 'fr', 'zh'];
/** Every UI language but the English source. */
export const TRANSLATION_LANGUAGES: readonly TranslationLanguage[] = ['es', 'pt', 'fr', 'zh'];
/** Review states, lowest first. */
export const REVIEW_STATES: readonly I18nReviewState[] = ['draft', 'machine', 'reviewed', 'approved'];
/** The states an entry may carry in R2. `approved` needs a named reviewer
 *  (B14), so an entry claiming it is malformed and never renders or counts
 *  toward the derived review (R2-B9) — same rule as the backend. */
export const WRITABLE_REVIEW_STATES: readonly I18nReviewState[] = REVIEW_STATES.filter((s) => s !== 'approved');

export function isTranslationLanguage(code: unknown): code is TranslationLanguage {
  return typeof code === 'string' && (TRANSLATION_LANGUAGES as readonly string[]).includes(code);
}

// ── sha256 ──────────────────────────────────────────────────────────────────

const toHex = (bytes: Uint8Array): string => Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

/** sha256 hex of the UTF-8 English, through Web Crypto. */
export async function sourceHash(text: string): Promise<string> {
  if (typeof text !== 'string') throw new TypeError('sourceHash: text must be a string');
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return toHex(new Uint8Array(digest));
}

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function sha256Bytes(msg: Uint8Array): Uint8Array {
  const bitLen = msg.length * 8;
  const padded = new Uint8Array(((msg.length + 9 + 63) >> 6) << 6);
  padded.set(msg);
  padded[msg.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(bitLen / 0x100000000));
  view.setUint32(padded.length - 4, bitLen >>> 0);
  const h = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);
  const w = new Uint32Array(64);
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));
  for (let off = 0; off < padded.length; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = h;
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + K[i] + w[i]) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      hh = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    h[0] += a; h[1] += b; h[2] += c; h[3] += d; h[4] += e; h[5] += f; h[6] += g; h[7] += hh;
  }
  const out = new Uint8Array(32);
  const outView = new DataView(out.buffer);
  h.forEach((v, i) => outView.setUint32(i * 4, v));
  return out;
}

const HASH_CACHE = new Map<string, string>();
const HASH_CACHE_MAX = 5000;

/** sha256 hex of the UTF-8 English, synchronously — for render paths. Memoised,
 *  because a form re-localises on every locale switch with the same English. */
export function sourceHashSync(text: string): string {
  if (typeof text !== 'string') throw new TypeError('sourceHashSync: text must be a string');
  const hit = HASH_CACHE.get(text);
  if (hit) return hit;
  const hex = toHex(sha256Bytes(new TextEncoder().encode(text)));
  if (HASH_CACHE.size >= HASH_CACHE_MAX) HASH_CACHE.clear();
  HASH_CACHE.set(text, hex);
  return hex;
}

// ── Entries ─────────────────────────────────────────────────────────────────

export type EntryState = 'missing' | 'invalid' | 'stale' | 'current';

const HASH_RE = /^[0-9a-f]{64}$/;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** A well-formed entry. The server validates strictly on write; a reader only
 *  needs to know it will not render garbage. */
export function isWellFormedEntry(entry: unknown): entry is I18nEntry {
  return isPlainObject(entry)
    && typeof entry.text === 'string' && entry.text.trim() !== ''
    && typeof entry.source_hash === 'string' && HASH_RE.test(entry.source_hash)
    && (WRITABLE_REVIEW_STATES as readonly unknown[]).includes(entry.status);
}

/** Same four states as the backend's `entryState`. */
export function entryState(entry: unknown, english: string | null | undefined): EntryState {
  if (entry === undefined || entry === null) return 'missing';
  if (!isWellFormedEntry(entry)) return 'invalid';
  if (typeof english !== 'string') return 'stale';
  return entry.source_hash === sourceHashSync(english) ? 'current' : 'stale';
}

/** A well-formed entry whose English has moved on. Missing is not stale. */
export function isStale(entry: unknown, english: string | null | undefined): boolean {
  return entryState(entry, english) === 'stale';
}

/** The only entries a reader may show in place of English. */
export function isCurrent(entry: unknown, english: string | null | undefined): entry is I18nEntry {
  return entryState(entry, english) === 'current';
}

// ── Slot enumeration (the same keys as the backend) ─────────────────────────

export interface TranslatableSlot {
  /** The translation-stage key, e.g. `field.patient.diagnosis.label`. */
  key: string;
  english: string;
  entry: I18nEntry | undefined;
  /** The R2-B10 offer gate requires this string. */
  gate: boolean;
}

type Visit = (slot: TranslatableSlot, set: (text: string) => void) => void;
// The walk reads and writes an untyped version body by path; one alias keeps
// the escape hatch in one place.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Obj = Record<string, any>;

const dig = (obj: unknown, ...keys: string[]): Obj | undefined =>
  keys.reduce<unknown>((o, k) => (isPlainObject(o) ? o[k] : undefined), obj) as Obj | undefined;

function copyLeaves(node: unknown, prefix: string, out: Array<[string, Obj | unknown[], string | number]>, parent: Obj | unknown[] | null, key: string | number | null) {
  if (typeof node === 'string') { if (parent !== null && key !== null) out.push([prefix, parent, key]); return; }
  if (Array.isArray(node)) { node.forEach((v, i) => copyLeaves(v, prefix ? `${prefix}.${i}` : String(i), out, node, i)); return; }
  if (isPlainObject(node)) {
    for (const [k, v] of Object.entries(node)) {
      if (k.endsWith('_url')) continue;
      copyLeaves(v, prefix ? `${prefix}.${k}` : k, out, node, k);
    }
  }
}

/** Walks every live, non-empty translatable English string of `body` in the
 *  backend's order; `set` writes a replacement into `body` itself. */
function walkSlots(body: Obj, code: string, visit: Visit): void {
  const emit = (key: string, english: unknown, entry: unknown, gate: boolean, set: (t: string) => void) => {
    if (typeof english === 'string' && english.trim() !== '') {
      visit({ key, english, entry: entry as I18nEntry | undefined, gate }, set);
    }
  };
  const gatedSections = new Set<string>();
  const gatedFields = new Set<string>();

  for (const f of (body.fields ?? []) as Obj[]) {
    if (f.retired) continue;
    const t = dig(f, 'i18n', code) ?? {};
    const base = `field.${f.section_id}.${f.id}`;
    const req = f.required === true;
    if (req) { gatedSections.add(f.section_id); gatedFields.add(`${f.section_id}.${f.id}`); }
    emit(`${base}.label`, f.label, t.label, req, (x) => { f.label = x; });
    for (const s of ['helper', 'placeholder', 'groupLabel']) emit(`${base}.${s}`, f[s], t[s], false, (x) => { f[s] = x; });
    const byLabel: Obj = f.conditionalLabel?.byValue ?? {};
    for (const dv of Object.keys(byLabel)) {
      // A required field's conditional label and options are what the family
      // reads for that question, so they gate the offer (R2-B10).
      emit(`${base}.conditionalLabel.${dv}`, byLabel[dv], dig(t, 'conditionalLabel', dv), req, (x) => { byLabel[dv] = x; });
    }
    for (const o of (f.options ?? []) as Obj[]) {
      if (o.retired) continue;
      emit(`${base}.options.${o.value}.label`, o.label, dig(t, 'options', o.value, 'label'), req, (x) => { o.label = x; });
      emit(`${base}.options.${o.value}.miss_line`, o.miss_line, dig(t, 'options', o.value, 'miss_line'), false, (x) => { o.miss_line = x; });
    }
    if (f.eligible_range) {
      const er = f.eligible_range as Obj;
      emit(`${base}.eligible_range.miss_line`, er.miss_line, dig(t, 'eligible_range', 'miss_line'), false, (x) => { er.miss_line = x; });
    }
    if (f.date_bound) {
      const db = f.date_bound as Obj;
      emit(`${base}.date_bound.message`, db.message, dig(t, 'date_bound', 'message'), false, (x) => { db.message = x; });
    }
    const byOpts: Obj = f.conditionalOptions?.byValue ?? {};
    for (const dv of Object.keys(byOpts)) {
      for (const o of (byOpts[dv] ?? []) as Obj[]) {
        emit(`${base}.conditionalOptions.${dv}.${o.value}.label`, o.label, dig(t, 'conditionalOptions', dv, o.value, 'label'), req, (x) => { o.label = x; });
      }
    }
  }
  for (const s of (body.sections ?? []) as Obj[]) {
    const t = dig(s, 'i18n', code) ?? {};
    emit(`section.${s.id}.title`, s.title, t.title, gatedSections.has(s.id), (x) => { s.title = x; });
    emit(`section.${s.id}.intro`, s.intro, t.intro, false, (x) => { s.intro = x; });
  }
  for (const s of (body.screens ?? []) as Obj[]) {
    const t = dig(s, 'i18n', code) ?? {};
    const groups = (s.groups ?? []) as Obj[];
    const holdsGated = groups.some((g) => ((g.field_ids ?? []) as Obj[]).some((r) => gatedFields.has(`${r.section_id}.${r.id}`)));
    emit(`screen.${s.id}.label`, s.label, t.label, false, (x) => { s.label = x; });
    emit(`screen.${s.id}.eyebrow`, s.eyebrow, t.eyebrow, false, (x) => { s.eyebrow = x; });
    emit(`screen.${s.id}.title`, s.title, t.title, holdsGated, (x) => { s.title = x; });
    groups.forEach((g, i) => emit(`screen.${s.id}.groups.${i}.label`, g.label, dig(t, 'groups', String(i), 'label'), false, (x) => { g.label = x; }));
  }
  const leaves: Array<[string, Obj | unknown[], string | number]> = [];
  copyLeaves(body.copy ?? {}, '', leaves, null, null);
  const copyT: Obj = dig(body, 'copy_i18n', code) ?? {};
  for (const [path, parent, k] of leaves) {
    emit(`copy.${path}`, (parent as Obj)[k], copyT[path], false, (x) => { (parent as Obj)[k] = x; });
  }
}

type I18nBody = Pick<FormDefinitionVersion, 'fields' | 'sections' | 'screens' | 'copy'> & { copy_i18n?: CopyI18n };

/** Every live translatable English string of a body, with `code`’s entry. */
export function translatableSlots(body: I18nBody, code: string): TranslatableSlot[] {
  const out: TranslatableSlot[] = [];
  walkSlots(body as Obj, code, (s) => out.push(s));
  return out;
}

/** The language review state the server derives (R2-B9). The client never
 *  stores it; the Languages tab shows what this returns. */
export function deriveLanguageReview(body: I18nBody, code: FormLanguage): I18nReviewState | null {
  if (code === 'en') return null;
  if (!isTranslationLanguage(code)) throw new RangeError(`deriveLanguageReview: ${code as string} is not a translation language`);
  const slots = translatableSlots(body, code);
  if (slots.length === 0) return 'draft';
  let lowest = REVIEW_STATES.length - 1;
  for (const s of slots) {
    if (!isCurrent(s.entry, s.english)) return 'draft';
    lowest = Math.min(lowest, REVIEW_STATES.indexOf(s.entry.status));
  }
  return REVIEW_STATES[lowest];
}

/** The R2-B10 offer gate: the keys that stop `code` being offered. */
export function offerGateFailures(body: I18nBody, code: FormLanguage): string[] {
  if (code === 'en') return [];
  return translatableSlots(body, code).filter((s) => s.gate && !isCurrent(s.entry, s.english)).map((s) => s.key);
}

/** Keys of `code` entries made from older English (the Languages tab count). */
export function staleKeys(body: I18nBody, code: FormLanguage): string[] {
  return translatableSlots(body, code).filter((s) => isStale(s.entry, s.english)).map((s) => s.key);
}

/**
 * Keys of `code` strings a family would read in English: no entry, a
 * malformed one, or one made from older English (#2109, Victor 2026-09-30 R2
 * — the Languages tab counts missing slots too, because the next publish
 * translates both).
 */
export function untranslatedKeys(body: I18nBody, code: FormLanguage): string[] {
  if (code === 'en') return [];
  return translatableSlots(body, code).filter((s) => !isCurrent(s.entry, s.english)).map((s) => s.key);
}

// ── The renderer's one call ─────────────────────────────────────────────────

export interface LocalizedForm {
  /** The version to render: strings replaced where a CURRENT translation
   *  exists, English everywhere else, and every `i18n`/`copy_i18n` subtree
   *  removed so no renderer can read an unchecked translation. A new object;
   *  the input is never mutated. */
  version: FormDefinitionVersion;
  /** The locale the reader asked for. */
  requested: string;
  /** The language the form is rendered in: `requested` when this version
   *  offers it, otherwise `en` (R2-B1). */
  locale: FormLanguage;
  /** `requested` is not English and this version does not offer it. The
   *  renderer shows the “shown in English” notice (R2-B1). */
  notOffered: boolean;
  /** At least one displayed string is a machine translation (status `draft`
   *  or `machine`). The renderer shows the one form-level line (R2-B18). */
  machine: boolean;
  /** Stage keys of strings shown in English because their translation is
   *  stale. The only per-string marker R2-B18 allows. */
  staleFallbacks: string[];
  /** `section.id` of each RETIRED field whose label stays English on a
   *  translated render (2026-10-11). Retired questions are asked nowhere and
   *  never gate the offer; the review step reads back answers to them, and
   *  marks these rows "shown in English". Empty on an English render. */
  retiredInEnglish?: string[];
}

/** `settings.offered_languages`, defaulting to English only. */
export function offeredLanguages(version: Pick<FormDefinitionVersion, 'settings'>): FormLanguage[] {
  const raw = version.settings?.offered_languages;
  if (!Array.isArray(raw)) return ['en'];
  const codes = raw.filter((c): c is FormLanguage => (UI_FORM_LANGUAGES as readonly unknown[]).includes(c));
  return codes.includes('en') ? codes : ['en', ...codes];
}

/**
 * #2374: a conditional select's "Select X first" hint names its driver by
 * `conditionalOptions.onLabel`, an English literal ("Region"). In a
 * translated render it names the driver by that field's own label, which the
 * walk has just translated, so the hint matches the question on screen. With
 * no driver field to name, `onLabel` is dropped and the hint falls back to the
 * translated "previous answer" sentence.
 */
function relabelConditionalDrivers(body: Obj): void {
  const fields = (body.fields ?? []) as Obj[];
  const byId = new Map(fields.map((f) => [f.id, f]));
  for (const f of fields) {
    const c = f.conditionalOptions as Obj | undefined;
    if (!c?.onLabel) continue;
    const driverLabel = byId.get(c.on)?.label;
    if (typeof driverLabel === 'string' && driverLabel) c.onLabel = driverLabel;
    else delete c.onLabel;
  }
}

function stripI18n(body: Obj): void {
  delete body.copy_i18n;
  for (const f of (body.fields ?? []) as Obj[]) delete f.i18n;
  for (const s of (body.sections ?? []) as Obj[]) delete s.i18n;
  for (const s of (body.screens ?? []) as Obj[]) delete s.i18n;
}

/**
 * Localise a published version for one reader. Pure and synchronous, so
 * `useFormDefinition` can call it once inside `useMemo` and every renderer
 * reads the result (plan lane L3).
 */
export function localizeVersion(version: FormDefinitionVersion, requested: string): LocalizedForm {
  const body = structuredClone(version) as FormDefinitionVersion & Obj;
  const offered = offeredLanguages(version);
  const canRender = isTranslationLanguage(requested) && offered.includes(requested);
  const result: LocalizedForm = {
    version: body,
    requested,
    locale: canRender ? requested : 'en',
    notOffered: requested !== 'en' && !canRender,
    machine: false,
    staleFallbacks: [],
    retiredInEnglish: [],
  };
  if (canRender) {
    // Collect first, then write, so every staleness check hashes the live
    // English and never a translation written earlier in the same walk.
    const writes: Array<[(t: string) => void, string]> = [];
    walkSlots(body, requested, (slot, set) => {
      const state = entryState(slot.entry, slot.english);
      if (state === 'current') {
        const e = slot.entry as I18nEntry;
        writes.push([set, e.text]);
        if (e.status === 'draft' || e.status === 'machine') result.machine = true;
      } else if (state === 'stale') {
        result.staleFallbacks.push(slot.key);
      }
    });
    for (const [set, text] of writes) set(text);
    relabelConditionalDrivers(body);
    // Retired questions sit outside the walk (they gate nothing and are asked
    // nowhere), but the review step reads back answers to them. A label
    // translation is the family's words for it; a missing or malformed one
    // stays English and is reported, so the row can say so (2026-10-11).
    //
    // #2611: a STALE translation counts here, unlike everywhere else. The walk
    // above skips retired fields, so a retired label is never re-translated —
    // and its English only ever moves when retiring touches the label (the
    // " (retired)" suffix) or when the question was reworded before it was
    // retired. The translation on file names the question the family actually
    // answered, in their language; English with "(shown in English)" beside
    // an answer they gave in French is the worse reading of both.
    for (const f of (body.fields ?? []) as Obj[]) {
      if (!f.retired || typeof f.label !== 'string') continue;
      const e = dig(f, 'i18n', requested, 'label');
      const state = entryState(e, f.label);
      if (state === 'current' || state === 'stale') {
        const entry = e as I18nEntry;
        f.label = entry.text;
        if (entry.status === 'draft' || entry.status === 'machine') result.machine = true;
      } else {
        result.retiredInEnglish!.push(`${f.section_id}.${f.id}`);
      }
    }
  }
  stripI18n(body);
  return result;
}
